import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import ScreenBackground from '../components/ScreenBackground';
import { Colors, SIZES, useThemeColors } from '../constants/theme';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { locationService } from '../services/locationService';
import { useGuardiaEscolar } from '../hooks/useGuardiaEscolar';

/** Genera un código de ruta corto, legible y memorable: R-XXXXXX */
function genRouteCode(driverId: string | null): string {
  // Si tenemos uid, usamos los primeros 6 chars como semilla determinista —
  // siempre el mismo código por driver para que los padres lo recuerden.
  const seed = (driverId ?? Math.random().toString(36).slice(2)).replace(/[^a-z0-9]/gi, '').slice(0, 6).toUpperCase();
  return `R-${seed.padEnd(6, 'X')}`;
}

export default function SchoolRouteScreen() {
  const colors = useThemeColors();
  const router = useRouter();

  const schoolDriverStatus = useAppStore((state) => state.schoolDriverStatus);
  const schoolTrialDaysLeft = useAppStore((state) => state.schoolTrialDaysLeft);
  const driverId = useAppStore((state) => state.userFirebaseUid);

  const [students, setStudents] = useState<any[]>([]);
  const [isRouteActive, setIsRouteActive] = useState(false);
  // Candado de "iniciar/terminar en vuelo": sin él, dos toques seguidos a
  // "Iniciar" creaban dos rutas y arrancaban el GPS dos veces. El ref cierra el
  // hueco entre el toque y el siguiente render (el estado aún diría "libre");
  // el estado es solo para atenuar el botón.
  const [procesandoRuta, setProcesandoRuta] = useState(false);
  const procesandoRef = useRef(false);

  // ══════════════════════════════════════════════════════════════════════════
  // EL GPS DE LA RUTA ESCOLAR NO TENÍA APAGADO (H30, auditoría 16-sep-2026).
  //
  // Era el único `startTracking` del proyecto sin su `stopTracking`: no había
  // botón de terminar, ni limpieza al salir de la pantalla, e `isRouteActive`
  // se escribía y nadie lo leía. Una vez iniciada la ruta, la posición del
  // chofer se seguía publicando en /driverLocations/{uid} después de entregar
  // al último niño — batería, cuota de Firestore y la ubicación en vivo de un
  // adulto expuesta sin motivo — hasta matar la app o cerrar sesión.
  //
  // Ahora hay dos salidas: el botón "Terminar Ruta" y el desmontaje de la
  // pantalla. Esto va con UBICACION_SEGUNDO_PLANO = false: el GPS vive solo
  // mientras la persona tiene la ruta delante, no detrás.
  //
  // Los refs existen porque la limpieza del desmontaje corre con los valores
  // del PRIMER render; el estado de React ahí siempre diría "ruta inactiva".
  //
  // OJO: `locationService` es uno solo para toda la app. Por eso se apaga
  // únicamente si fue ESTA pantalla la que lo encendió (`rutaActivaRef`), y no
  // "por si acaso": apagarlo a ciegas le cortaría el GPS a un chofer conectado
  // desde el mapa.
  // ══════════════════════════════════════════════════════════════════════════
  const rutaActivaRef = useRef(false);
  const montadaRef = useRef(true);
  // V49 — id del documento /school_routes de la ruta en curso, para cerrarla
  // (status 'finished') al terminar. Antes quedaba 'active' para siempre.
  const rutaIdRef = useRef<string | null>(null);
  const cerrarRutaEnFirestore = async () => {
    const id = rutaIdRef.current;
    if (!id) return;
    await firestoreService.finishSchoolRoute(id);
    rutaIdRef.current = null;
  };
  useEffect(() => {
    montadaRef.current = true;
    return () => {
      montadaRef.current = false;
      if (rutaActivaRef.current) {
        rutaActivaRef.current = false;
        locationService.stopTracking().catch((e) =>
          console.warn('[school-route] no se pudo apagar el GPS al salir de la pantalla:', e)
        );
        // Sin GPS la ruta ya no se comparte: que tampoco figure como activa.
        cerrarRutaEnFirestore().catch((e) =>
          console.warn('[school-route] no se pudo cerrar la ruta al salir de la pantalla:', e)
        );
      }
    };
  }, []);

  const routeCode = useMemo(() => genRouteCode(driverId), [driverId]);

  // Listener en tiempo real de estudiantes inscritos en la ruta de este driver.
  // Las reglas Firestore: solo el driver propio o el director de su escuela leen `students`
  // donde `driver === driverId`.
  useEffect(() => {
    if (!driverId) return;
    const unsub = firestoreService.listenToRouteStudents(driverId, (list) => {
      setStudents(list);
    });
    return () => unsub();
  }, [driverId]);

  const handleBoardStudent = async (id: string, currentStatus: string) => {
    const nextStatus =
      currentStatus === 'Esperando abordaje' ? 'A bordo' :
      currentStatus === 'A bordo' ? 'Entregado' : currentStatus;

    // Optimismo + persistencia: el listener traerá la actualización oficial.
    setStudents(prev => prev.map(s => s.id === id ? { ...s, status: nextStatus } : s));
    try {
      await firestoreService.updateStudentRouteStatus(id, nextStatus);
    } catch (e) {
      console.warn('No se pudo actualizar status del estudiante:', e);
      Alert.alert('Error', 'No se pudo persistir el cambio de estado. Reintenta cuando tengas conexión.');
    }
  };

  const handleStartRoute = async () => {
    if (procesandoRef.current || rutaActivaRef.current) return;
    if (!driverId) {
      Alert.alert('Sesión requerida', 'Inicia sesión como chofer escolar para iniciar la ruta.');
      return;
    }
    procesandoRef.current = true;
    setProcesandoRuta(true);
    let gpsEncendidoAqui = false;
    try {
      // Arrancar el GPS PRIMERO (mismo servicio que rides; en primer plano
      // mientras UBICACION_SEGUNDO_PLANO = false). El doc /driverLocations/{uid}
      // se actualiza para que padres y director vean la posición.
      await locationService.startTracking(driverId);

      // `startTracking` vuelve callado si se negó el permiso. Una ruta marcada
      // "activa" sin GPS le diría a los padres que ven el bus cuando no ven nada.
      const gpsVivo = Platform.OS === 'web' || locationService.modoDeRastreoActual() !== null;
      if (!gpsVivo) {
        if (montadaRef.current) {
          Alert.alert(
            'Activa la ubicación',
            'Sin permiso de ubicación no se puede compartir la ruta con los padres. Actívalo en los ajustes del teléfono e inténtalo otra vez.'
          );
        }
        return;
      }
      gpsEncendidoAqui = Platform.OS !== 'web';

      // Si la persona salió de la pantalla mientras el GPS arrancaba, la
      // limpieza del desmontaje ya pasó y no lo vio encendido: se apaga aquí,
      // o quedaría emitiendo sin nadie que lo pueda parar (H30).
      if (!montadaRef.current) {
        await locationService.stopTracking();
        return;
      }

      // El schoolId (uid del director) lo pone el servicio desde el perfil del
      // chofer: aquí antes iba el uid del propio chofer y ningún director la veía.
      rutaIdRef.current = await firestoreService.createSchoolRoute(driverId, {
        routeName: `Ruta Escolar - ${new Date().toLocaleDateString()}`,
        routeCode,
        status: 'active',
        startedAt: new Date().toISOString(),
      });

      // Mismo caso que arriba, durante la escritura de la ruta.
      if (!montadaRef.current) {
        await locationService.stopTracking();
        await cerrarRutaEnFirestore().catch(() => {});
        return;
      }

      rutaActivaRef.current = true;
      setIsRouteActive(true);
      Alert.alert("Ruta Iniciada", "La ubicación en tiempo real se está compartiendo con los padres y el director. Toca «Terminar Ruta» al entregar al último alumno para dejar de compartirla.");
    } catch (error) {
      console.error('startRoute error', error);
      // Si la ruta no se pudo registrar, el GPS que se encendió para ella no
      // puede quedar emitiendo.
      if (gpsEncendidoAqui) await locationService.stopTracking().catch(() => {});
      if (montadaRef.current) Alert.alert("Error", "No se pudo iniciar la ruta. Inténtalo de nuevo.");
    } finally {
      procesandoRef.current = false;
      if (montadaRef.current) setProcesandoRuta(false);
    }
  };

  const handleEndRoute = async () => {
    if (procesandoRef.current) return;
    procesandoRef.current = true;
    setProcesandoRuta(true);
    try {
      // Se marca ANTES del await: si la pantalla se desmonta a mitad, la
      // limpieza no intenta apagar por segunda vez lo que ya se está apagando.
      rutaActivaRef.current = false;
      await locationService.stopTracking();
      // El GPS ya está apagado: si cerrar el documento falla, no se vuelve a
      // encender nada; queda anotado y la ruta se da por terminada en pantalla.
      await cerrarRutaEnFirestore().catch((e) =>
        console.warn('[school-route] no se pudo marcar la ruta como terminada:', e)
      );
      if (!montadaRef.current) return;
      setIsRouteActive(false);
      Alert.alert('Ruta terminada', 'Ya no se está compartiendo tu ubicación.');
    } catch (error) {
      console.error('endRoute error', error);
      // El GPS pudo quedar encendido: se devuelve la ruta a "activa" para que
      // el botón siga ofreciendo terminarla y el desmontaje lo vuelva a intentar.
      rutaActivaRef.current = true;
      Alert.alert('Error', 'No se pudo apagar la ubicación. Inténtalo de nuevo.');
    } finally {
      procesandoRef.current = false;
      if (montadaRef.current) setProcesandoRuta(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await Clipboard.setStringAsync(routeCode);
      Alert.alert('Copiado', `Código ${routeCode} copiado al portapapeles.`);
    } catch {
      Alert.alert('Error', 'No se pudo copiar el código.');
    }
  };

  // Vertical escolar oculto: ver VERTICAL_ESCOLAR_VISIBLE en featureFlags.ts
  const escolarOculto = useGuardiaEscolar();
  if (escolarOculto) return null;

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={[styles.header, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text }]}>Mi Ruta Escolar</Text>
          <TouchableOpacity onPress={() => router.push('/(tabs)/profile')}>
            <Ionicons name="settings-outline" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.container}>
          
          {/* BANNER INTELIGENTE DE PRUEBA/MERCADO */}
          {schoolDriverStatus === 'SEARCHING' ? (
            <View style={[styles.statusBanner, { backgroundColor: 'rgba(33, 150, 243, 0.1)', borderColor: '#2196F3' }]}>
              <Ionicons name="search" size={24} color="#2196F3" />
              <View style={styles.statusBannerText}>
                <Text style={[styles.statusBannerTitle, { color: '#2196F3' }]}>Buscando Contratos</Text>
                <Text style={styles.statusBannerDesc}>
                  ¡No pagas cuota en este modo! Tu prueba gratis de 30 días iniciará automáticamente en cuanto un estudiante se vincule a tu ruta.
                </Text>
              </View>
            </View>
          ) : schoolDriverStatus === 'TRIAL' ? (
            <View style={[styles.statusBanner, { backgroundColor: 'rgba(255, 149, 0, 0.1)', borderColor: '#FF9500' }]}>
              <Ionicons name="flash" size={24} color="#FF9500" />
              <View style={styles.statusBannerText}>
                <Text style={[styles.statusBannerTitle, { color: '#FF9500' }]}>¡Ruta Activa! Prueba Gratis</Text>
                <Text style={styles.statusBannerDesc}>
                  Te quedan {schoolTrialDaysLeft} días de prueba gratis. Demuestra la calidad de tu servicio a los padres.
                </Text>
              </View>
            </View>
          ) : null}

          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Text style={[styles.cardTitle, { color: colors.text }]}>Tu Código de Ruta</Text>
            <Text style={[styles.cardDescription, { color: colors.textSecondary }]}>
              Comparte este código seguro con los padres para que puedan vincular a sus hijos a tu vehículo y rastrear el trayecto en tiempo real.
            </Text>
            <View style={[styles.codeBtn, { backgroundColor: 'rgba(0, 45, 98, 0.05)' }]}>
              <Text style={[styles.codeText, { color: colors.primary }]}>{routeCode}</Text>
              <TouchableOpacity onPress={handleCopyCode}>
                <Ionicons name="copy" size={24} color={colors.primary} />
              </TouchableOpacity>
            </View>
          </View>

          <View style={{ flexDirection: 'row', gap: 10, marginBottom: 30 }}>
            {/* Con la ruta en marcha el mismo botón pasa a "Terminar Ruta"
                (H30): antes no existía ninguna forma de apagar el GPS. */}
            <TouchableOpacity
              style={[
                styles.routeActionBtn,
                { backgroundColor: isRouteActive ? colors.error : colors.primary, flex: 1, opacity: procesandoRuta ? 0.6 : 1 },
              ]}
              onPress={isRouteActive ? handleEndRoute : handleStartRoute}
              disabled={procesandoRuta}
            >
              <Ionicons name={isRouteActive ? 'stop' : 'play'} size={20} color="#FFF" />
              <Text style={styles.routeActionBtnText}>{isRouteActive ? 'Terminar Ruta' : 'Iniciar Ruta Ahora'}</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.routeActionBtn, { backgroundColor: colors.accent, paddingHorizontal: 15 }]}
              onPress={() => {
                const code = Math.random().toString(36).substring(2, 8).toUpperCase();
                Alert.alert(
                  'Código de Invitación Generado',
                  `Código: ${code}\n\nDile al padre: "Descargue la aplicación Go Safely y use este código para registrar a su hijo y poder rastrear mi vehículo en tiempo real."\n\nEl estudiante aparecerá aquí una vez el padre complete el registro.`,
                  [
                    // Antes este 'Copiar' solo mostraba "Código copiado al
                    // portapapeles" sin copiar nada: el padre pegaba y no
                    // había nada. Ahora copia de verdad.
                    {
                      text: 'Copiar',
                      onPress: async () => {
                        try {
                          await Clipboard.setStringAsync(code);
                          Alert.alert('Copiado', `Código ${code} copiado al portapapeles.`);
                        } catch {
                          Alert.alert('No se pudo copiar', `Anota el código: ${code}`);
                        }
                      },
                    },
                    { text: 'Cerrar', style: 'cancel' }
                  ]
                );
              }}
            >
              <Ionicons name="person-add" size={20} color="#FFF" />
            </TouchableOpacity>
          </View>

          {/* LISTA DE ALUMNOS */}
          <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>ALUMNOS EN ESTA RUTA ({students.length})</Text>
          {students.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              <Ionicons name="people-outline" size={48} color={colors.border} />
              <Text style={{ color: colors.textSecondary, marginTop: 12, textAlign: 'center' }}>
                No tienes alumnos inscritos en esta ruta aún. Comparte tu código de ruta con los padres.
              </Text>
            </View>
          ) : (
            students.map((student) => (
              <View key={student.id} style={[styles.studentRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.studentInfo}>
                  <Text style={[styles.studentName, { color: colors.text }]}>{student.name}</Text>
                  <Text style={[styles.studentSchool, { color: colors.textSecondary }]}>{student.school}</Text>
                  <Text style={[styles.studentStatus, { color: student.status === 'A bordo' ? '#4CAF50' : student.status === 'Entregado' ? '#2196F3' : '#FF9800' }]}>
                    Estado: {student.status}
                  </Text>
                </View>
                <TouchableOpacity
                  style={[styles.statusToggleBtn, { backgroundColor: student.status === 'Esperando abordaje' ? colors.primary : student.status === 'A bordo' ? '#4CAF50' : '#757575' }]}
                  onPress={() => handleBoardStudent(student.id, student.status)}
                  disabled={student.status === 'Entregado'}
                >
                  <Text style={styles.statusToggleBtnText}>
                    {student.status === 'Esperando abordaje' ? 'Abordar' : student.status === 'A bordo' ? 'Entregar' : 'Completado'}
                  </Text>
                </TouchableOpacity>
              </View>
            ))
          )}

          <View style={{ height: 100 }} />
        </ScrollView>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  container: {
    padding: 20,
  },
  card: {
    borderRadius: 20,
    padding: 20,
    marginBottom: 25,
    borderWidth: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  cardDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 15,
  },
  codeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 15,
    borderRadius: 12,
  },
  codeText: {
    fontSize: 20,
    fontWeight: 'bold',
    letterSpacing: 2,
  },
  routeActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 3,
  },
  routeActionBtnText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 15,
    marginLeft: 4,
    letterSpacing: 1,
  },
  studentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
  },
  studentInfo: {
    flex: 1,
    paddingRight: 10,
  },
  studentName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  studentSchool: {
    fontSize: 13,
    marginBottom: 4,
  },
  studentStatus: {
    fontSize: 13,
    fontWeight: '600',
  },
  statusToggleBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
  },
  statusToggleBtnText: {
    color: '#FFF',
    fontSize: 13,
    fontWeight: 'bold',
  },
  statusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 15,
    borderWidth: 1,
    marginBottom: 20,
  },
  statusBannerText: {
    marginLeft: 15,
    flex: 1,
  },
  statusBannerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  statusBannerDesc: {
    fontSize: 13,
    color: '#333',
    lineHeight: 18,
  }
});

